/* global SideDrawer */

// ---------------------------------------------------------------------------
// <stl-variant-select>
//
// Lightweight self-contained variant selector for shop-the-look cards.
// No section fetching, no URL updates — just reads the embedded variant JSON,
// matches the selected option values to a variant, and dispatches a custom
// 'stl:variant:change' event that <shop-the-look-drawer> listens to.
// ---------------------------------------------------------------------------

if (!customElements.get('stl-variant-select')) {
  class StlVariantSelect extends HTMLElement {
    connectedCallback() {
      this.selects = this.querySelectorAll('select');
      this.swatchInputs = this.querySelectorAll('.js-stl-swatch-input');
      this.variants = JSON.parse(this.querySelector('[type="application/json"]').textContent);

      this.boundHandleChange = this.handleChange.bind(this);

      this.selects.forEach((select) => {
        select.addEventListener('change', this.boundHandleChange);
      });

      this.swatchInputs.forEach((input) => {
        input.addEventListener('change', this.boundHandleChange);
      });

      // Resolve and announce the initially selected variant on load
      // so the add-to-cart button starts in the correct state.
      this.announceVariant(this.findMatchingVariant());
    }

    /**
     * Handles 'change' events on any option <select> or swatch radio input.
     * Attempts a direct variant match first. If none found and a Style Number
     * option exists, auto-selects the first available Style Number that yields
     * a valid variant for the other selected options.
     */
    handleChange(evt) {
      // Update the visible color label if a swatch was clicked.
      if (evt.target.classList.contains('js-stl-swatch-input')) {
        this.updateSwatchLabel(evt.target);
      }

      const variant = this.findMatchingVariant();

      if (variant) {
        this.announceVariant(variant);
        return;
      }

      // No direct match — try auto-selecting a Style Number if one exists.
      if (this.hasStyleNumber()) {
        const resolved = this.resolveStyleNumber();
        if (resolved) {
          this.announceVariant(resolved);
          return;
        }
      }

      // Truly no matching variant.
      this.announceVariant(null);
    }

    /**
     * Updates the visible color name label when a swatch is selected.
     * Mirrors the .js-color-text pattern from variant-picker.
     * @param {Element} input - The checked swatch radio input.
     */
    updateSwatchLabel(input) {
      const optionName = input.dataset.optionName;
      const group = input.closest('.stl-variant-select__option');
      const label = group?.querySelector('.js-stl-color-text');
      if (label) label.textContent = input.value;
    }

    /**
     * Returns true if the first option is "Style Number".
     * Checks data-option-name on the first select element.
     * @returns {boolean}
     */
    hasStyleNumber() {
      const firstSelect = this.selects[0];
      return firstSelect?.dataset.optionName?.toLowerCase() === 'style number';
    }

    /**
     * Iterates all Style Number values and finds the first available variant
     * that matches the currently selected non-Style-Number options.
     * Silently updates the Style Number <select> to the matched value.
     * @returns {object|null} The matched variant, or null if none found.
     */
    resolveStyleNumber() {
      const styleSelect = this.selects[0];
      const selectedOptions = this.getSelectedOptions();

      for (const option of styleSelect.options) {
        const styleValue = option.value;
        const testOptions = [...selectedOptions];
        testOptions[0] = styleValue;

        const match = this.variants.find((variant) =>
          variant.available &&
          variant.options.every((opt, i) => opt === testOptions[i])
        );

        if (match) {
          // Silently update the Style Number select to the matched value
          // without triggering another change event.
          styleSelect.value = styleValue;
          return match;
        }
      }

      return null;
    }

    /**
     * Returns the currently selected option values across all inputs.
     * Reads selects in DOM order, and for swatch groups reads the checked radio.
     * The order matches product.options order since both selects and swatch groups
     * are rendered in option order in the Liquid.
     * @returns {string[]}
     */
    getSelectedOptions() {
      const options = [];

      this.querySelectorAll('.stl-variant-select__option:not(.stl-variant-select__option--hidden)').forEach((group) => {
        const select = group.querySelector('select');
        if (select) {
          options.push(select.value);
          return;
        }
        const checked = group.querySelector('.js-stl-swatch-input:checked');
        if (checked) {
          options.push(checked.value);
          return;
        }
        options.push(null);
      });

      // Prepend Style Number value if present (always option[0], always a select).
      const styleSelect = this.selects[0];
      if (styleSelect?.dataset.optionName?.toLowerCase() === 'style number') {
        options.unshift(styleSelect.value);
      }

      return options;
    }

    /**
     * Finds the variant whose options exactly match the current selections.
     * @returns {object|null}
     */
    findMatchingVariant() {
      const selected = this.getSelectedOptions();
      return this.variants.find((variant) =>
        variant.options.every((opt, i) => opt === selected[i])
      ) || null;
    }

    /**
     * Dispatches 'stl:variant:change' so the card and drawer can react.
     * @param {object|null} variant
     */
    announceVariant(variant) {
      this.dispatchEvent(new CustomEvent('stl:variant:change', {
        bubbles: true,
        detail: { variant }
      }));
    }
  }

  customElements.define('stl-variant-select', StlVariantSelect);
}

// ---------------------------------------------------------------------------
// <shop-the-look-drawer>
// ---------------------------------------------------------------------------

if (!customElements.get('shop-the-look-drawer')) {
  class ShopTheLookDrawer extends SideDrawer {
    constructor() {
      super();

      // Bind handlers once in constructor so we can add/remove them reliably.
      this.boundVariantChange = this.handleVariantChange.bind(this);
      this.boundClick = this.handleAddToCart.bind(this);

      // Move into the overlay group so it renders above other elements.
      // This triggers a disconnect + reconnect, so listeners must live in
      // connectedCallback (not here) to avoid being registered twice.
      document.addEventListener('DOMContentLoaded', () => {
        const overlayGroup = document.querySelector('.overlay-addon-drawers');
        if (overlayGroup) overlayGroup.appendChild(this);
      });
    }

    connectedCallback() {
      // Called once on first connect, and again after appendChild moves the
      // element. Remove before adding to guarantee only one registration.
      this.removeEventListener('stl:variant:change', this.boundVariantChange);
      this.removeEventListener('click', this.boundClick);

      this.addEventListener('stl:variant:change', this.boundVariantChange);
      this.addEventListener('click', this.boundClick);
    }

    disconnectedCallback() {
      this.removeEventListener('stl:variant:change', this.boundVariantChange);
      this.removeEventListener('click', this.boundClick);
    }

    /**
     * Handles 'stl:variant:change' events bubbled up from <stl-variant-select>.
     * Updates the hidden id input and the visible add-to-cart button for the card.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      const { variant } = evt.detail;
      const card = evt.target.closest('.stl-product-card');
      if (!card) return;

      const idInput = card.querySelector('input[name="id"]');
      const addBtn = card.querySelector('[data-stl-add-to-cart]');
      const addToCartText = addBtn?.dataset.addToCartText || 'Add to bag';
      const soldOutText = addBtn?.dataset.soldOutText || 'Sold out';
      const unavailableText = addBtn?.dataset.unavailableText || 'Unavailable';

      if (variant) {
        // Update hidden id input so the form always reflects the right variant
        if (idInput) idInput.value = variant.id;

        // Update visible button
        if (addBtn) {
          addBtn.dataset.variantId = variant.id;

          if (variant.available) {
            addBtn.disabled = false;
            addBtn.textContent = addToCartText;
          } else {
            addBtn.disabled = true;
            addBtn.textContent = soldOutText;
          }
        }

        // Update card image if this variant has a featured image
        if (variant.featured_image) {
          this.updateCardImage(card, variant.featured_image);
        }

      } else {
        // No matching variant for the selected combination
        if (idInput) idInput.value = '';
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = unavailableText;
          delete addBtn.dataset.variantId;
        }
      }
    }

    /**
     * Handles delegated 'click' events — catches [data-stl-add-to-cart] buttons.
     * NOTE: Named handleAddToCart (not handleClick) to avoid overriding SideDrawer's
     * handleClick, which wires up .js-close-drawer and overlay close via open().
     * @param {object} evt - Event object.
     */
    handleAddToCart(evt) {
      const btn = evt.target.closest('[data-stl-add-to-cart]');
      if (!btn) return;
      evt.preventDefault();
      this.addToCart(btn);
    }

    /**
     * Updates the product image in a card when a variant with a featured image is selected.
     * @param {Element} card - The product card element.
     * @param {object} featuredImage - Variant featured_image object from Shopify.
     */
    updateCardImage(card, featuredImage) {
      const imgEl = card.querySelector('.stl-product-card__img');
      if (!imgEl || !featuredImage?.src) return;

      const width = imgEl.offsetWidth || 200;
      const src = featuredImage.src;

      imgEl.src = `${src}&width=${width}`;
      imgEl.srcset = `${src}&width=${width} 1x, ${src}&width=${width * 2} 2x`;
      imgEl.alt = featuredImage.alt || '';
    }

    /**
     * Adds the selected variant to the cart via AJAX.
     * @param {Element} btn - The "Add to Bag" button that was clicked.
     */
    async addToCart(btn) {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      const card = btn.closest('.stl-product-card');

      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-loading');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        if (response.ok) {
          // Refresh the cart drawer count using the theme's event — does not trigger another add.
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
          this.showNotification(card, btn.dataset.addedText || 'Added to bag!', 'success');
        } else {
          const data = await response.json();
          this.showNotification(card, data.description || 'Could not add to cart.', 'error');
        }
      } catch (err) {
        console.error('[ShopTheLookDrawer] addToCart error:', err);
      } finally {
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('is-loading');
      }
    }

    /**
     * Shows a temporary notification message on a product card.
     * @param {Element} card
     * @param {string} message
     * @param {'success'|'error'} type
     */
    showNotification(card, message, type) {
      const el = card?.querySelector('.stl-product-card__notification');
      if (!el) return;

      el.textContent = message;
      el.className = `stl-product-card__notification stl-product-card__notification--${type} text-sm`;
      el.hidden = false;

      setTimeout(() => {
        el.hidden = true;
      }, type === 'error' ? 5000 : 3000);
    }
  }

  customElements.define('shop-the-look-drawer', ShopTheLookDrawer);
}

// ---------------------------------------------------------------------------
// <shop-the-look-trigger>
// ---------------------------------------------------------------------------

if (!customElements.get('shop-the-look-trigger')) {
  class ShopTheLookTrigger extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (this.button) {
        this.button.addEventListener('click', this.handleClick.bind(this));
      }
    }

    handleClick() {
      const drawer = document.getElementById(this.dataset.drawerId);
      if (drawer) drawer.open(this.button);
    }
  }

  customElements.define('shop-the-look-trigger', ShopTheLookTrigger);
}